import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Plus, MoreHorizontal, Edit, Trash2, Loader2, Building2, MapPin, Phone, Mail, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';

const OfficesPage = () => {
  const { api } = useAuth();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    is_head_office: false
  });

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      const response = await api().get('/offices');
      setOffices(response.data);
    } catch (error) {
      toast.error('Failed to load offices');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingOffice(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      is_head_office: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (office) => {
    setEditingOffice(office);
    setFormData({
      name: office.name,
      address: office.address || '',
      city: office.city || '',
      country: office.country || '',
      phone: office.phone || '',
      email: office.email || '',
      is_head_office: office.is_head_office || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Office name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingOffice) {
        await api().put(`/offices/${editingOffice.id}`, formData);
        toast.success('Office updated successfully');
      } else {
        await api().post('/offices', formData);
        toast.success('Office created successfully');
      }
      setDialogOpen(false);
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save office');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (office) => {
    if (office.is_head_office) {
      toast.error('Cannot delete head office');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete "${office.name}"?`)) return;
    
    try {
      await api().delete(`/offices/${office.id}`);
      toast.success('Office deleted successfully');
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete office');
    }
  };

  // Pre-render office rows to avoid babel issue
  const officeRows = [];
  for (let i = 0; i < offices.length; i++) {
    const office = offices[i];
    officeRows.push(
      <TableRow key={office.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{office.name}</span>
            {office.is_head_office && (
              <Badge variant="secondary" className="gap-1">
                <Crown className="w-3 h-3" />
                Head Office
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {office.address && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="w-3 h-3" />
              {office.address}
            </div>
          )}
        </TableCell>
        <TableCell>{office.city || '-'}</TableCell>
        <TableCell>{office.country || '-'}</TableCell>
        <TableCell>
          {office.phone && (
            <div className="flex items-center gap-1 text-sm">
              <Phone className="w-3 h-3 text-muted-foreground" />
              {office.phone}
            </div>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>{office.user_count || 0}</span>
          </div>
        </TableCell>
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditDialog(office)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {!office.is_head_office && (
                <DropdownMenuItem onClick={() => handleDelete(office)} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Branch Offices</h1>
          <p className="text-muted-foreground">Manage your organization's branch offices</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-office-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Office
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            All Offices
          </CardTitle>
          <CardDescription>
            {offices.length} office{offices.length !== 1 ? 's' : ''} registered
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {offices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Building2 className="w-8 h-8 mb-2 opacity-50" />
              <p>No offices created yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Office Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead className="w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{officeRows}</TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Office Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOffice ? 'Edit Office' : 'Create New Office'}</DialogTitle>
            <DialogDescription>
              {editingOffice ? 'Update office details' : 'Add a new branch office to your organization'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Office Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Lahore Branch"
                data-testid="office-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Street address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+92 xxx xxxxxxx"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="branch@company.com"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label>Head Office</Label>
                <p className="text-sm text-muted-foreground">Set as the main head office</p>
              </div>
              <Switch
                checked={formData.is_head_office}
                onCheckedChange={(checked) => setFormData({ ...formData, is_head_office: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-office-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingOffice ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficesPage;
